import {
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlinePlayCircle,
} from "react-icons/ai";
import React, { useState, useEffect } from "react";
import { Modal } from "antd";

export default function MediaCarousel({ mediaItems = [], getImageUrl }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Reset index when items change
  useEffect(() => {
    setCurrentIndex(0);
  }, [mediaItems]);

  const currentItem = mediaItems[currentIndex];
  const hasMultiple = mediaItems.length > 1;

  const handleNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const handlePrev = (e) => {
    e?.stopPropagation();
    setCurrentIndex(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length,
    );
  };

  const handleThumbnailClick = (index, e) => {
    e?.stopPropagation();
    setCurrentIndex(index);
  };

  const renderMediaItem = (item, isPreview = false) => {
    const url = getImageUrl(item);
    if (item.type === "VIDEO") {
      return (
        <div className="relative w-full h-full flex items-center justify-center bg-black">
          <video
            src={isPreview ? url : `${url}#t=0.1`}
            controls={isPreview}
            className={`w-full h-full ${isPreview ? "object-contain" : "object-contain"}`}
            style={{ maxHeight: isPreview ? "65vh" : "500px" }}
            onClick={(e) => !isPreview && e.stopPropagation()}
            preload="metadata"
          />
          {!isPreview && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
              <AiOutlinePlayCircle className="text-white text-6xl opacity-80" />
            </div>
          )}
        </div>
      );
    }
    return (
      <img
        src={url}
        alt="media"
        className={`w-full h-full ${isPreview ? "object-contain" : "object-contain bg-black"}`}
        style={{ maxHeight: isPreview ? "65vh" : "500px" }}
      />
    );
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen]);

  if (!mediaItems || mediaItems.length === 0) return null;

  return (
    <div className="w-full select-none">
      {/* Main Display */}
      <div
        className="relative group w-full bg-black rounded-xl overflow-hidden cursor-pointer"
        style={{ height: "250px" }}
        onClick={() => setLightboxOpen(true)}
      >
        <div className="w-full h-full flex items-center justify-center">
          {renderMediaItem(currentItem)}
        </div>

        {/* Navigation Arrows (Only if multiple) */}
        {hasMultiple && (
          <>
            {/* Left Arrow */}
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 
                 flex items-center justify-center
                 w-10 h-10
                 rounded-full 
                 bg-gray-600/40 text-gray-200 
                 hover:bg-gray-600/60 
                 transition-all 
                 opacity-0 group-hover:opacity-100"
            >
              <AiOutlineLeft size={24} />
            </button>

            {/* Right Arrow */}
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 
                 flex items-center justify-center
                 w-10 h-10
                 rounded-full 
                 bg-gray-600/40 text-gray-200 
                 hover:bg-gray-600/60 
                 transition-all 
                 opacity-0 group-hover:opacity-100"
            >
              <AiOutlineRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails Strip */}
      {hasMultiple && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2 px-2">
          {mediaItems.map((item, index) => (
            <div
              key={index}
              onClick={(e) => handleThumbnailClick(index, e)}
              className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all p-1 ${
                index === currentIndex
                  ? "border-blue-500 opacity-100"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              {item.type === "VIDEO" ? (
                <div className="w-full h-full bg-gray-900 flex items-center justify-center relative">
                  <video
                    src={`${getImageUrl(item)}#t=0.1`}
                    className="w-full h-full object-cover"
                    preload="metadata"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <AiOutlinePlayCircle className="text-white text-2xl" />
                  </div>
                </div>
              ) : (
                <img
                  src={getImageUrl(item)}
                  alt="thumb"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <Modal
        visible={lightboxOpen}
        footer={null}
        title={null}
        closable={false}
        centered
        width="90%"
        style={{ maxWidth: "1400px" }}
        bodyStyle={{
          padding: 0,
          maxHeight: "85vh",
          backgroundColor: "rgba(0,0,0,0.95)",
          borderRadius: "8px",
          position: "relative",
        }}
        onCancel={() => setLightboxOpen(false)}
      >
        <div className="relative w-full" style={{ minHeight: "70vh" }}>
          {/* Navigation Arrows - Left and Right sides */}
          {hasMultiple && (
            <>
              <div className="absolute left-0 inset-y-0 flex items-center z-50 pl-6">
                <button
                  onClick={handlePrev}
                  className="p-3 text-gray-300 hover:text-white bg-gray-800/40 hover:bg-gray-800/60 rounded-full transition-colors shadow-lg"
                >
                  <AiOutlineLeft size={36} />
                </button>
              </div>
              <div className="absolute right-0 inset-y-0 flex items-center z-50 pr-6">
                <button
                  onClick={handleNext}
                  className="p-3 text-gray-300 hover:text-white bg-gray-800/40 hover:bg-gray-800/60 rounded-full transition-colors shadow-lg"
                >
                  <AiOutlineRight size={36} />
                </button>
              </div>
            </>
          )}

          {/* Media Content */}
          <div className="w-full h-full flex items-center justify-center p-8">
            {renderMediaItem(currentItem, true)}
          </div>

          {/* Bottom Thumbnails in Lightbox */}
          {hasMultiple && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 overflow-x-auto px-4 pb-2 z-40">
              {mediaItems.map((item, index) => (
                <div
                  key={index}
                  onClick={(e) => handleThumbnailClick(index, e)}
                  className={`w-12 h-12 rounded border cursor-pointer transition-all p-0.5 ${
                    index === currentIndex
                      ? "border-white opacity-100"
                      : "border-transparent opacity-50 hover:opacity-100"
                  }`}
                >
                  {item.type === "VIDEO" ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                      <video
                        src={`${getImageUrl(item)}#t=0.1`}
                        className="w-full h-full object-cover"
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <AiOutlinePlayCircle className="text-white text-xl" />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={getImageUrl(item)}
                      alt="thumb"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
